import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, Calendar, UtensilsCrossed, Music, MessageSquare, User, LogOut, Menu, Settings, Send, Gamepad2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { getSeasonalLogo } from '../lib/seasonalLogo';

const LOGO_URL = getSeasonalLogo();

const navItems = [
  { to: '/', label: 'Accueil', icon: Home },
  { to: '/prestations', label: 'Prestations', icon: Music },
  { to: '/events', label: 'Evenements', icon: Calendar },
  { to: '/carte', label: 'Carte', icon: UtensilsCrossed },
  { to: '/rejoindre', label: 'Jouer', icon: Gamepad2 },
  { to: '/reservation', label: 'Reserver', icon: Send },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Desktop Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 h-16 flex items-center px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
          <img src={LOGO_URL} alt="RSX" className="h-9 w-9 rounded-full" />
          <span className="font-semibold text-sm tracking-wide hidden sm:inline" style={{ fontFamily: 'Outfit' }}>RSX CONCEPT</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-8">
          {navItems.map(({ to, label }) => (
            <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                location.pathname === to ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
              }`}>
              {label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <>
              {user.role === 'admin' && (
                <Link to="/admin" data-testid="admin-link">
                  <Button variant="ghost" size="sm" className="text-[#D4AF37] hover:bg-[#D4AF37]/10">
                    <Settings className="w-4 h-4 mr-1" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/mon-espace" data-testid="espace-link">
                <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-white">
                  <User className="w-4 h-4 mr-1" /> Mon Espace
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"
                className="text-[#A1A1AA] hover:text-white h-8 w-8">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="login-link">
                <Button variant="ghost" size="sm" className="text-white hover:bg-white/5">Connexion</Button>
              </Link>
              <Link to="/register" data-testid="register-link">
                <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-5">Inscription</Button>
              </Link>
            </>
          )}
          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0A0A0A] border-white/10 w-64">
              <div className="flex flex-col gap-2 mt-8">
                {navItems.map(({ to, label, icon: Icon }) => (
                  <Link key={to} to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                    location.pathname === to ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'text-[#A1A1AA] hover:text-white'
                  }`}>
                    <Icon className="w-4 h-4" /> {label}
                  </Link>
                ))}
                {user && (
                  <>
                    <Link to="/mon-espace" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[#A1A1AA] hover:text-white">
                      <User className="w-4 h-4" /> Mon Espace
                    </Link>
                    <Link to="/contact" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[#A1A1AA] hover:text-white">
                      <MessageSquare className="w-4 h-4" /> Contact
                    </Link>
                  </>
                )}
                {!user && (
                  <Link to="/contact" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[#A1A1AA] hover:text-white">
                    <MessageSquare className="w-4 h-4" /> Contact
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 pb-20 md:pb-0 min-h-screen">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="bottom-nav fixed bottom-0 left-0 right-0 z-50 md:hidden flex items-center justify-around h-16 px-2">
        {navItems.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} data-testid={`bottomnav-${label.toLowerCase()}`}
            className={`flex flex-col items-center gap-0.5 px-3 py-1 ${
              location.pathname === to ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
            }`}>
            <Icon className="w-5 h-5" />
            <span className="text-[10px]">{label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
